import React, { useState } from "react"
import { ArrowLeft, ArrowRight, Plus, X, Type, List, Hash, ToggleLeft, ClipboardList } from "lucide-react"

// ── Shared primitives (matching CreateJobForm exactly) ────────────────────────

const FieldLabel = ({ children, required }) => (
  <label
    style={{
      display: "block",
      fontSize: 13,
      fontWeight: 600,
      color: "#374151",
      marginBottom: 6,
      letterSpacing: "0.01em",
    }}
  >
    {children}
    {required && <span style={{ color: "#ef4444", marginLeft: 2 }}>*</span>}
  </label>
)

const SectionLabel = ({ children }) => (
  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6, marginBottom: 2 }}>
    <span
      style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: "#6b7280",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
    <div style={{ flex: 1, height: 1, background: "#f3f4f6" }} />
  </div>
)

// ── Question type config ──────────────────────────────────────────────────────

const QUESTION_TYPES = [
  { id: "free_text", label: "Free Text", icon: Type, mono: "TXT" },
  { id: "multiple_choice", label: "Multiple Choice", icon: List, mono: "MCQ" },
  { id: "number", label: "Number", icon: Hash, mono: "NUM" },
  { id: "yes_no", label: "Yes / No", icon: ToggleLeft, mono: "Y/N" },
]

const EXAMPLE_QUESTIONS = [
  { id: "ex_1", text: "What experience do you have selling SaaS products to SMBs?", type: "free_text" },
  { id: "ex_2", text: "What experience do you have with sales quotas?", type: "free_text" },
]

const uid = () => `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`

const typeMonoLabel = (id) => QUESTION_TYPES.find((t) => t.id === id)?.mono ?? "?"

// ── Type badge ────────────────────────────────────────────────────────────────

const TypeBadge = ({ typeId }) => (
  <span
    style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      padding: "2px 6px",
      background: "#f3f4f6",
      color: "#6b7280",
      border: "1px solid #e5e7eb",
      borderRadius: 4,
      fontFamily: "monospace",
      flexShrink: 0,
    }}
  >
    {typeMonoLabel(typeId)}
  </span>
)

// ── Add Question Modal (CreateJobForm style) ──────────────────────────────────

const AddQuestionModal = ({ onAdd, onClose, initialType = "free_text" }) => {
  const [questionText, setQuestionText] = useState("")
  const [selectedType, setSelectedType] = useState(initialType)
  const [options, setOptions] = useState(["", ""])
  const [required, setRequired] = useState(false)
  const [optionError, setOptionError] = useState("")

  const handleSubmit = () => {
    if (!questionText.trim()) return
    if (selectedType === "multiple_choice") {
      const filled = options.filter((o) => o.trim())
      if (filled.length < 2) { setOptionError("Add at least 2 options."); return }
      onAdd({ id: uid(), text: questionText.trim(), type: selectedType, required, options: filled })
    } else {
      onAdd({ id: uid(), text: questionText.trim(), type: selectedType, required, options: [] })
    }
    onClose()
  }

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 50,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <div
        style={{
          background: "#fff",
          border: "1px solid #e5e7eb",
          borderRadius: 12,
          width: "100%",
          maxWidth: 440,
          margin: "0 16px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.12)",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "16px 20px",
            borderBottom: "1px solid #f3f4f6",
          }}
        >
          <span style={{ fontSize: 14, fontWeight: 700, color: "#111827", letterSpacing: "-0.01em" }}>
            New Question
          </span>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "#9ca3af", padding: 2, display: "flex" }}
          >
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>

        <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Question text */}
          <div>
            <FieldLabel required>Question text</FieldLabel>
            <textarea
              rows={2}
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="e.g. What is your current notice period?"
              style={{
                width: "100%",
                padding: "10px 12px",
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 6,
                fontSize: 14,
                fontWeight: 500,
                color: "#111827",
                fontFamily: "'DM Sans', sans-serif",
                outline: "none",
                resize: "none",
                boxSizing: "border-box",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#6b7280")}
              onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
            />
          </div>

          {/* Type selector */}
          <div>
            <FieldLabel>Answer type</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
              {QUESTION_TYPES.map(({ id, label, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setSelectedType(id)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 12px",
                    border: `1px solid ${selectedType === id ? "#111827" : "#e5e7eb"}`,
                    borderRadius: 6,
                    background: selectedType === id ? "#111827" : "#fff",
                    color: selectedType === id ? "#fff" : "#374151",
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    fontFamily: "'DM Sans', sans-serif",
                    transition: "all 0.12s",
                    textAlign: "left",
                  }}
                >
                  <Icon size={13} strokeWidth={2.5} />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Options for multiple choice */}
          {selectedType === "multiple_choice" && (
            <div>
              <FieldLabel>Options</FieldLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {options.map((opt, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <input
                      type="text"
                      value={opt}
                      onChange={(e) => setOptions((p) => p.map((o, idx) => (idx === i ? e.target.value : o)))}
                      placeholder={`Option ${i + 1}`}
                      style={{
                        flex: 1,
                        height: 36,
                        padding: "0 12px",
                        border: "1px solid #e5e7eb",
                        borderRadius: 6,
                        fontSize: 13,
                        fontWeight: 500,
                        color: "#111827",
                        fontFamily: "'DM Sans', sans-serif",
                        outline: "none",
                      }}
                    />
                    {options.length > 2 && (
                      <button
                        type="button"
                        onClick={() => setOptions((p) => p.filter((_, idx) => idx !== i))}
                        style={{ background: "none", border: "none", cursor: "pointer", color: "#d1d5db", display: "flex" }}
                      >
                        <X size={13} strokeWidth={2.5} />
                      </button>
                    )}
                  </div>
                ))}
                {optionError && <p style={{ fontSize: 12, color: "#ef4444", marginTop: 2 }}>{optionError}</p>}
                <button
                  type="button"
                  onClick={() => setOptions((p) => [...p, ""])}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 13,
                    fontWeight: 500,
                    color: "#6b7280",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    padding: 0,
                    fontFamily: "'DM Sans', sans-serif",
                  }}
                >
                  <Plus size={12} strokeWidth={2.5} />
                  Add option
                </button>
              </div>
            </div>
          )}

          {/* Required toggle */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <button
              type="button"
              onClick={() => setRequired((p) => !p)}
              style={{
                width: 16,
                height: 16,
                border: `1px solid ${required ? "#111827" : "#d1d5db"}`,
                borderRadius: 4,
                background: required ? "#111827" : "#fff",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                flexShrink: 0,
                padding: 0,
              }}
            >
              {required && (
                <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              )}
            </button>
            <label
              onClick={() => setRequired((p) => !p)}
              style={{ fontSize: 13, fontWeight: 500, color: "#374151", cursor: "pointer", userSelect: "none" }}
            >
              Mark as required
            </label>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            gap: 8,
            padding: "14px 20px",
            borderTop: "1px solid #f3f4f6",
          }}
        >
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: "8px 16px",
              fontSize: 13,
              fontWeight: 500,
              color: "#374151",
              background: "#fff",
              border: "1px solid #d1d5db",
              borderRadius: 7,
              cursor: "pointer",
              fontFamily: "'DM Sans', sans-serif",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!questionText.trim()}
            style={{
              padding: "8px 18px",
              fontSize: 13,
              fontWeight: 600,
              color: "#fff",
              background: questionText.trim() ? "#111827" : "#9ca3af",
              border: "1px solid transparent",
              borderRadius: 7,
              cursor: questionText.trim() ? "pointer" : "not-allowed",
              fontFamily: "'DM Sans', sans-serif",
              transition: "background 0.12s",
            }}
          >
            Add Question
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Question row ──────────────────────────────────────────────────────────────

const QuestionRow = ({ question, onToggleRequired, onDelete }) => {
  const [hovered, setHovered] = useState(false)
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "10px 12px",
        border: `1px solid ${hovered ? "#d1d5db" : "#e5e7eb"}`,
        borderRadius: 6,
        background: "#fafafa",
        transition: "border-color 0.12s",
      }}
    >
      <TypeBadge typeId={question.type} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <p
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: "#111827",
            margin: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {question.text}
        </p>
        {question.type === "multiple_choice" && question.options?.length > 0 && (
          <p style={{ fontSize: 11, color: "#9ca3af", margin: "2px 0 0", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {question.options.join(" · ")}
          </p>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
        {question.required && (
          <span style={{ fontSize: 10, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            REQ
          </span>
        )}
        {/* Required checkbox */}
        <button
          type="button"
          title={question.required ? "Mark optional" : "Mark required"}
          onClick={() => onToggleRequired(question.id)}
          style={{
            width: 14,
            height: 14,
            border: `1px solid ${question.required ? "#111827" : "#d1d5db"}`,
            borderRadius: 3,
            background: question.required ? "#111827" : "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            padding: 0,
            flexShrink: 0,
          }}
        >
          {question.required && (
            <svg width="7" height="7" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </button>
        {/* Delete */}
        <button
          type="button"
          onClick={() => onDelete(question.id)}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: hovered ? "#6b7280" : "#d1d5db",
            display: "flex",
            padding: 0,
            transition: "color 0.12s",
          }}
        >
          <X size={13} strokeWidth={2.5} />
        </button>
      </div>
    </div>
  )
}

// ── Main Component ────────────────────────────────────────────────────────────

const ApplicationFormStep = ({ formData, setFormData, onBack, onSubmit, isSubmitting }) => {
  const applicationForm = formData?.applicationForm || {}
  const [questions, setQuestions] = useState(applicationForm.questions ?? [])
  const [showAddModal, setShowAddModal] = useState(false)
  const [modalInitialType, setModalInitialType] = useState("free_text")

  const persist = (nextQuestions) => {
    setFormData((prev) => ({ ...prev, applicationForm: { questions: nextQuestions } }))
  }

  const toggleRequired = (id) => {
    const next = questions.map((q) => (q.id === id ? { ...q, required: !q.required } : q))
    setQuestions(next)
    persist(next)
  }

  const deleteQuestion = (id) => {
    const next = questions.filter((q) => q.id !== id)
    setQuestions(next)
    persist(next)
  }

  const handleAddQuestion = (q) => {
    const next = [...questions, q]
    setQuestions(next)
    persist(next)
  }

  return (
    <>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700&display=swap'); * { box-sizing: border-box; }`}</style>

      <div style={{ fontFamily: "'DM Sans', sans-serif", paddingBottom: 80 }}>

        {/* Page heading */}
        <div style={{ marginBottom: 24 }}>
          <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: "0 0 4px", letterSpacing: "-0.02em" }}>
            Pre-Interview Form
          </h3>
          <p style={{ fontSize: 13, color: "#6b7280", margin: 0, fontWeight: 400 }}>
            These questions help you shortlist faster
          </p>
        </div>

        {/* Two-column layout */}
        <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>

          {/* ── Left 60%: form questions ── */}
          <div style={{ flex: "0 0 60%", display: "flex", flexDirection: "column", gap: 0 }}>

            {/* Panel */}
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              {/* Panel header */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: "16px 20px",
                  borderBottom: "1px solid #f3f4f6",
                }}
              >
                <ClipboardList size={14} strokeWidth={2.5} style={{ color: "#6b7280", flexShrink: 0 }} />
                <div>
                  <p style={{ fontSize: 13, fontWeight: 700, color: "#111827", margin: 0, letterSpacing: "-0.01em" }}>
                    Pre-Interview Questions
                  </p>
                  <p style={{ fontSize: 12, color: "#6b7280", margin: 0, fontWeight: 400 }}>
                    Candidates answer these before the interview
                  </p>
                </div>
              </div>

              <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: 16 }}>

                {/* Empty state */}
                {questions.length === 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      padding: "32px 16px",
                      border: "1px dashed #e5e7eb",
                      borderRadius: 8,
                      background: "#f9fafb",
                    }}
                  >
                    <ClipboardList size={20} strokeWidth={1.5} style={{ color: "#d1d5db", marginBottom: 8 }} />
                    <p style={{ fontSize: 13, fontWeight: 500, color: "#6b7280", margin: "0 0 2px" }}>
                      No questions added yet
                    </p>
                    <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>
                      Create a custom question below
                    </p>
                  </div>
                )}

                {/* Question list */}
                {questions.length > 0 && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {questions.map((q) => (
                      <QuestionRow
                        key={q.id}
                        question={q}
                        onToggleRequired={toggleRequired}
                        onDelete={deleteQuestion}
                      />
                    ))}
                  </div>
                )}

                <SectionLabel>Add custom question</SectionLabel>

                {/* Type quick-add buttons */}
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {QUESTION_TYPES.map(({ id, label, icon: Icon }) => (
                      <button
                        key={id}
                        type="button"
                        onClick={() => { setModalInitialType(id); setShowAddModal(true) }}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 6,
                          padding: "7px 12px",
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#374151",
                          border: "1px solid #e5e7eb",
                          borderRadius: 6,
                          background: "#fff",
                          cursor: "pointer",
                          fontFamily: "'DM Sans', sans-serif",
                          transition: "border-color 0.12s, background 0.12s",
                        }}
                        onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.background = "#f9fafb" }}
                        onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; e.currentTarget.style.background = "#fff" }}
                      >
                        <Icon size={12} strokeWidth={2.5} style={{ color: "#9ca3af" }} />
                        {label}
                      </button>
                    ))}
                  </div>
                  <p style={{ fontSize: 13, color: "#9ca3af", margin: 0 }}>
                    Click any type above to create a custom question
                  </p>
                </div>

              </div>
            </div>
          </div>

          {/* ── Right 40%: Example Questions ── */}
          <div style={{ flex: "0 0 calc(40% - 20px)" }}>
            <div
              style={{
                background: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                overflow: "hidden",
              }}
            >
              {/* Panel header */}
              <div style={{ padding: "16px 20px", borderBottom: "1px solid #f3f4f6" }}>
                <p style={{ fontSize: 13, fontWeight: 700, color: "#111827", margin: "0 0 2px", letterSpacing: "-0.01em" }}>
                  Example Questions
                </p>
                <p style={{ fontSize: 12, color: "#6b7280", margin: 0, fontWeight: 400 }}>
                  Common pre-screening questions
                </p>
              </div>

              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: 8 }}>
                {EXAMPLE_QUESTIONS.map((q) => (
                  <div
                    key={q.id}
                    style={{
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 10,
                      padding: "10px 12px",
                      border: "1px solid #f3f4f6",
                      borderRadius: 6,
                      background: "#fafafa",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p
                        style={{
                          fontSize: 13,
                          fontWeight: 500,
                          color: "#374151",
                          margin: "0 0 5px",
                          lineHeight: 1.4,
                        }}
                      >
                        {q.text}
                      </p>
                      <TypeBadge typeId={q.type} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Add Question Modal */}
      {showAddModal && (
        <AddQuestionModal
          onAdd={handleAddQuestion}
          onClose={() => setShowAddModal(false)}
          initialType={modalInitialType}
        />
      )}

      {/* Fixed bottom nav */}
      <div
        style={{
          position: "fixed",
          bottom: 0,
          left: 240,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #f3f4f6",
          padding: "12px 32px",
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          fontFamily: "'DM Sans', sans-serif",
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 18px",
            fontSize: 13,
            fontWeight: 600,
            color: "#374151",
            background: "#fff",
            border: "1px solid #d1d5db",
            borderRadius: 8,
            cursor: "pointer",
            fontFamily: "'DM Sans', sans-serif",
            transition: "all 0.12s",
            letterSpacing: "-0.01em",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#9ca3af"; e.currentTarget.style.color = "#111827" }}
          onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#d1d5db"; e.currentTarget.style.color = "#374151" }}
        >
          <ArrowLeft size={13} strokeWidth={2.5} />
          Back
        </button>

        <p style={{ fontSize: 12, color: "#9ca3af", margin: 0 }}>
          {questions.length} question{questions.length !== 1 ? "s" : ""} · {questions.filter((q) => q.required).length} required
        </p>

        <button
          type="button"
          onClick={onSubmit}
          disabled={isSubmitting}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "9px 20px",
            fontSize: 13,
            fontWeight: 600,
            color: "#fff",
            background: isSubmitting ? "#6b7280" : "#111827",
            border: "1px solid transparent",
            borderRadius: 8,
            cursor: isSubmitting ? "not-allowed" : "pointer",
            fontFamily: "'DM Sans', sans-serif",
            letterSpacing: "-0.01em",
            transition: "background 0.12s",
          }}
          onMouseEnter={(e) => { if (!isSubmitting) e.currentTarget.style.background = "#27272a" }}
          onMouseLeave={(e) => { if (!isSubmitting) e.currentTarget.style.background = "#111827" }}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Creating...
            </>
          ) : (
            <>
              Create Job
              <ArrowRight size={13} strokeWidth={2.5} />
            </>
          )}
        </button>
      </div>
    </>
  )
}

export default ApplicationFormStep